import { useCallback, useEffect, useRef, useState } from 'react';

export type ZoomLevel = 0 | 1 | 2 | 3;

export const ZOOM_LEVEL_KEYS = ['year', 'month', 'week', 'day'] as const;
export type ZoomLevelKey = (typeof ZOOM_LEVEL_KEYS)[number];

export function startOfMondayWeek(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  const day = r.getDay();
  r.setDate(r.getDate() + (day === 0 ? -6 : 1 - day));
  return r;
}

export function getWeekWindowStart(viewDate: Date, windowDays: number): Date {
  const anchor = stripTime(viewDate);
  const daysBefore = Math.floor((windowDays - 1) / 2);
  const start = new Date(anchor);
  start.setDate(start.getDate() - daysBefore);
  return start;
}

export function stripTime(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

export function useZoomCalendar(initialLevel: ZoomLevel = 2) {
  const [level, setLevel] = useState<ZoomLevel>(initialLevel);
  const [viewDate, setViewDate] = useState(() => stripTime(new Date()));
  const levelRef = useRef(level);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  const zoomIn = useCallback((targetDate?: Date) => {
    if (targetDate) setViewDate(stripTime(targetDate));
    setLevel(l => (l >= 3 ? l : ((l + 1) as ZoomLevel)));
  }, []);

  const zoomOut = useCallback(() => {
    setLevel(l => (l <= 0 ? l : ((l - 1) as ZoomLevel)));
  }, []);

  const jumpToLevel = useCallback((target: ZoomLevel) => {
    setLevel(target);
  }, []);

  const navigate = useCallback((delta: number, opts?: { weekStepDays?: number }) => {
    const weekStep = opts?.weekStepDays ?? 7;
    setViewDate(prev => {
      const next = new Date(prev);
      const l = levelRef.current;
      if (l === 0) next.setFullYear(next.getFullYear() + delta);
      else if (l === 1) next.setMonth(next.getMonth() + delta);
      else if (l === 2) next.setDate(next.getDate() + delta * weekStep);
      else next.setDate(next.getDate() + delta);
      return stripTime(next);
    });
  }, []);

  const goToToday = useCallback(() => {
    setViewDate(stripTime(new Date()));
  }, []);

  return {
    level,
    setLevel,
    viewDate,
    setViewDate,
    zoomIn,
    zoomOut,
    jumpToLevel,
    navigate,
    goToToday,
  };
}
